import { useState, type FormEvent } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export default function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signup, isAuthenticated } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [inviteCode, setInviteCode] = useState(() => (
    (searchParams.get('invite') || '').replace(/\D/g, '').slice(0, 6)
  ));
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 이미 로그인된 상태면 홈으로
  if (isAuthenticated) {
    navigate('/', { replace: true });
    return null;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    const result = await signup(name, email, password, passwordConfirm, inviteCode);

    setIsSubmitting(false);

    if (result.success) {
      navigate('/', { replace: true });
    } else {
      setError(result.error || '회원가입에 실패했습니다');
    }
  };

  return (
    <div className="min-h-screen max-w-lg mx-auto bg-background-50 flex flex-col">
      {/* 상단 영역 */}
      <div className="relative w-full pt-12 pb-8 px-6 flex flex-col items-center text-center bg-gradient-to-b from-accent-100/60 via-secondary-100/40 to-background-50">
        <div className="absolute top-6 left-6">
          <Link
            to="/login"
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-background-50/70 hover:bg-background-50 text-foreground-600 hover:text-foreground-900 transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg"></i>
          </Link>
        </div>

        {/* 장식용 원형 요소 */}
        <div className="absolute top-8 right-8 w-16 h-16 rounded-full bg-accent-200/30"></div>

        <div className="relative z-10">
          <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-accent-500 flex items-center justify-center">
            <i className="ri-user-heart-line text-2xl text-background-50"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-950 mb-1">
            가족 회원가입
          </h1>
          <p className="text-sm text-foreground-500">
            초대코드를 입력하고 우리 가족과 함께하세요
          </p>
        </div>
      </div>

      {/* 회원가입 폼 */}
      <div className="flex-1 px-6 pb-10">
        <div className="bg-background-50 rounded-2xl p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 에러 메시지 */}
            {error && (
              <div className="bg-accent-100/50 border border-accent-300/50 rounded-xl px-4 py-3 flex items-start gap-2.5">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-error-warning-line text-sm text-accent-600"></i>
                </div>
                <p className="text-sm text-accent-700 leading-relaxed">{error}</p>
              </div>
            )}

            {/* 이름 */}
            <div>
              <label htmlFor="signup-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                이름
              </label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line text-base text-foreground-400"></i>
                </div>
                <input
                  id="signup-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="홍길동"
                  autoComplete="name"
                  className="w-full h-12 pl-10 pr-4 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
                />
              </div>
            </div>

            {/* 이메일 */}
            <div>
              <label htmlFor="signup-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                이메일
              </label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-line text-base text-foreground-400"></i>
                </div>
                <input
                  id="signup-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="family@example.com"
                  autoComplete="email"
                  className="w-full h-12 pl-10 pr-4 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
                />
              </div>
            </div>

            {/* 비밀번호 */}
            <div>
              <label htmlFor="signup-password" className="block text-sm font-medium text-foreground-700 mb-1.5">
                비밀번호
              </label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-lock-line text-base text-foreground-400"></i>
                </div>
                <input
                  id="signup-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="6자 이상 입력"
                  autoComplete="new-password"
                  className="w-full h-12 pl-10 pr-12 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200 transition-colors cursor-pointer"
                  aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-base text-foreground-400`}></i>
                </button>
              </div>
            </div>

            {/* 비밀번호 확인 */}
            <div>
              <label htmlFor="signup-password-confirm" className="block text-sm font-medium text-foreground-700 mb-1.5">
                비밀번호 확인
              </label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-shield-check-line text-base text-foreground-400"></i>
                </div>
                <input
                  id="signup-password-confirm"
                  type={showPasswordConfirm ? 'text' : 'password'}
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  placeholder="비밀번호를 다시 입력하세요"
                  autoComplete="new-password"
                  className="w-full h-12 pl-10 pr-12 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200 transition-colors cursor-pointer"
                  aria-label={showPasswordConfirm ? '비밀번호 숨기기' : '비밀번호 보기'}
                >
                  <i className={`${showPasswordConfirm ? 'ri-eye-off-line' : 'ri-eye-line'} text-base text-foreground-400`}></i>
                </button>
              </div>
              {/* 비밀번호 일치 여부 실시간 표시 */}
              {passwordConfirm && password && (
                <p className={`text-xs mt-1.5 ml-1 ${password === passwordConfirm ? 'text-green-600' : 'text-accent-600'}`}>
                  {password === passwordConfirm ? (
                    <span className="flex items-center gap-1">
                      <i className="ri-check-line"></i> 비밀번호가 일치합니다
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <i className="ri-close-line"></i> 비밀번호가 일치하지 않습니다
                    </span>
                  )}
                </p>
              )}
            </div>

            {/* 초대코드 */}
            <div>
              <label htmlFor="signup-invite-code" className="block text-sm font-medium text-foreground-700 mb-1.5">
                가족 초대코드
              </label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-key-2-line text-base text-foreground-400"></i>
                </div>
                <input
                  id="signup-invite-code"
                  type="text"
                  value={inviteCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setInviteCode(val);
                  }}
                  placeholder="123456"
                  autoComplete="off"
                  maxLength={6}
                  inputMode="numeric"
                  className="w-full h-12 pl-10 pr-4 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all tracking-[0.3em] text-center"
                />
              </div>
              <p className="text-xs text-foreground-400 mt-1.5 ml-1">
                가족 대표에게 받은 6자리 숫자 코드를 입력해주세요
              </p>
            </div>

            {/* 회원가입 버튼 */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-12 bg-accent-500 hover:bg-accent-600 disabled:bg-accent-300 text-background-50 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin"></div>
                  <span>가입 중...</span>
                </>
              ) : (
                '회원가입'
              )}
            </button>
          </form>

          {/* 로그인으로 돌아가기 */}
          <div className="mt-6 text-center">
            <p className="text-xs text-foreground-500">
              이미 가족 계정이 있으신가요?{' '}
              <Link to="/login" className="text-accent-600 hover:text-accent-700 font-medium cursor-pointer">
                로그인
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
